import { CSSProperties } from "react"

export function processTemplate(template: string, variables: Record<string, any>): string {
  let processed = template

  for (const [key, value] of Object.entries(variables)) {
    const regex = new RegExp(`{{\\s*${key}\\s*}}`, "g")
    processed = processed.replace(regex, String(value || ""))
  }

  return processed
}

function lightenColor(hex: string, percent: number): string {
  let r = parseInt(hex.slice(1, 3), 16);
  let g = parseInt(hex.slice(3, 5), 16);
  let b = parseInt(hex.slice(5, 7), 16);

  r = Math.min(255, Math.floor(r + (255 - r) * percent));
  g = Math.min(255, Math.floor(g + (255 - g) * percent));
  b = Math.min(255, Math.floor(b + (255 - b) * percent));

  const toHex = (c: number) => c.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function darkenColor(hex: string, percent: number): string {
  let r = parseInt(hex.slice(1, 3), 16);
  let g = parseInt(hex.slice(3, 5), 16);
  let b = parseInt(hex.slice(5, 7), 16);

  r = Math.max(0, Math.floor(r * (1 - percent)));
  g = Math.max(0, Math.floor(g * (1 - percent)));
  b = Math.max(0, Math.floor(b * (1 - percent)));

  const toHex = (c: number) => c.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export const getContactStatusColor = (color?: string): CSSProperties => {
  const fallbackHex = "#6b7280"; // gray-500

  if (!color || typeof color !== "string") {
    return {
      "--status-bg-rgb": "107, 114, 128",
      "--status-color-light": "#4b5563", // gray-600
      "--status-color-dark": "#d1d5db",  // gray-300
    } as CSSProperties;
  }

  // Ensure it starts with #
  const hex = color.startsWith("#") ? color : `#${color}`;

  // Validate hex
  const isValidHex = /^#([0-9A-F]{3}|[0-9A-F]{6})$/i.test(hex);
  const finalHex = isValidHex ? hex : fallbackHex;

  // Convert 3-digit hex to 6-digit
  let fullHex = finalHex;
  if (finalHex.length === 4) {
    fullHex = `#${finalHex[1]}${finalHex[1]}${finalHex[2]}${finalHex[2]}${finalHex[3]}${finalHex[3]}`;
  }

  const r = parseInt(fullHex.slice(1, 3), 16);
  const g = parseInt(fullHex.slice(3, 5), 16);
  const b = parseInt(fullHex.slice(5, 7), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  // Calculate high-contrast/readable colors for light and dark modes
  let lightModeText = fullHex;
  let darkModeText = fullHex;

  // In light mode, if the color is too light (high brightness), darken it
  if (brightness > 180) {
    lightModeText = brightness > 240 ? "#1f2937" : darkenColor(fullHex, 0.4);
  }

  // In dark mode, if the color is too dark (low brightness), lighten it
  if (brightness < 80) {
    darkModeText = brightness < 15 ? "#f3f4f6" : lightenColor(fullHex, 0.6);
  }

  return {
    "--status-bg-rgb": `${r}, ${g}, ${b}`,
    "--status-color-light": lightModeText,
    "--status-color-dark": darkModeText,
  } as CSSProperties;
};



export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}
